/**
 * The site footer, and it is a trust surface rather than a sitemap.
 *
 * Everything on this page is here because a stranger who has not decided about
 * us yet needs it: who builds this, where to read what we keep, and who to
 * write to. Nothing is here because footers usually have it. There is no
 * newsletter box, no status badge, no second copy of the marketing headline,
 * and no column of links to pages that do not exist.
 *
 * IT IS TYPOGRAPHY, NOT A SLAB. The footer of most consoles is a dark plate
 * bolted to the bottom of the document, which is a way of saying "the page
 * ended a while ago". This one has one hairline and a lot of air, and it takes
 * its ground from whatever chapter it lands under. On the landing that ground
 * is the ink band the waitlist sits on, so the page still lands on one surface
 * rather than on a seam. See the :has rule in site-footer.css.
 *
 * TWO MARKS, AND THEY ARE NOT THE SAME MARK. The lockup at the top is SV
 * Cloud's own, and it is the Wordmark component the landing top bar uses, so
 * the bar and the footer can never drift apart. The small tile on the line
 * below it is SV Academy's, and it is the dark terminal tile in
 * public/brand/sv_logo_512.png. That tile is the only legal SV Academy mark
 * that exists. If a different SV logo ever turns up in a deck, a template or a
 * search result, it is not ours, and it does not go here.
 *
 * THE PARENTAGE LINE IS A FOUNDER REQUIREMENT, not a nicety. SV Cloud is a new
 * address asking people for an email, and the fastest honest answer to "who is
 * this" is the school that has been running for years under the same company.
 * So the sentence is plain, and the address is printed under it rather than
 * hidden behind the words, which is the same rule every SV lockup follows.
 *
 * THE SOCIAL ADDRESSES ARE COPIED, NEVER REMEMBERED. All five are below as
 * literal constants. Two of them are easy to get wrong from memory (the base
 * is siliconvalley.academy, not svacademy, and the Facebook page has no vanity
 * name yet, only a numeric id), and a footer link that lands on somebody else's
 * account is worse than no link at all. Change one only against the founder's
 * own list.
 */

import { Link } from 'react-router';
import { PrivacyChoicesLink } from '../patterns/ConsentNotice';

import { Wordmark } from '../brand/Wordmark';
import { getToken, useSessionStore } from '../../lib/auth/session';
import { t } from '../../lib/i18n/t';

import '../../styles/components/site-footer.css';

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

/* --------------------------------------------------------- the addresses */

/**
 * Addresses and registered names are facts, not copy, so they are values here
 * rather than keys in the string catalogue. A translator has nothing to do to
 * a URL, and a URL that went through t() would be a URL a mistranslation could
 * break.
 */
const SV_ACADEMY_URL = 'https://sv-academy.org';
const SV_ACADEMY_HOST = 'sv-academy.org';

/**
 * The registered Thai company name, exactly as filed. It reads identically in
 * every locale, which is what makes it a value and not a translated string.
 * Retyping Thai from memory is how a legal name ships wrong, so this line is
 * copied and never re-keyed.
 */
const COMPANY_TH = 'บริษัท ซิลิคอน วัลเลย์ อินโนเวชั่น จำกัด';

const SOCIAL_URLS = {
  instagram: 'https://www.instagram.com/siliconvalley.academy',
  tiktok: 'https://www.tiktok.com/@siliconvalley.academy',
  youtube: 'https://www.youtube.com/@SVAcademyTH',
  facebook: 'https://www.facebook.com/profile.php?id=61590784208590',
  line: 'https://lin.ee/5mwmz71',
} as const;

/* ------------------------------------------------------------- the footer */

export function SiteFooter() {
  const sessionStatus = useSessionStore((s) => s.status);
  const isLoggedIn =
    sessionStatus === 'signed-in' || (!USE_MOCK && sessionStatus === 'loading' && !!getToken());

  /**
   * Built inside the component, not at module scope, because t() reads the
   * language that is current at render. A list of labels frozen at import time
   * would be a list of English labels for the life of the tab.
   *
   * The network names still go through t() even though four of the five are
   * invariant proper nouns. The rule is worth more than the exception: a
   * catalogue with a hole in it is a catalogue nobody trusts, and LINE is
   * written in Thai script in plenty of Thai contexts, so the hole would not
   * even have been a safe one.
   */
  const social = [
    { label: t('Instagram'), href: SOCIAL_URLS.instagram },
    { label: t('TikTok'), href: SOCIAL_URLS.tiktok },
    { label: t('YouTube'), href: SOCIAL_URLS.youtube },
    { label: t('Facebook'), href: SOCIAL_URLS.facebook },
    { label: t('LINE'), href: SOCIAL_URLS.line },
  ];

  return (
    <footer className="site-foot">
      <div className="site-foot__inner">
        <div className="site-foot__brand">
          <Link className="site-foot__lockup" to="/" aria-label={t('SV Cloud')}>
            <Wordmark />
          </Link>

          <p className="site-foot__by">
            {/* alt is empty on purpose: the sentence beside it already says SV
                Academy, and a screen reader that hears the name twice is being
                told the picture is a second fact. */}
            <img className="site-foot__tile" src="/brand/sv_logo_512.png" alt="" />
            <span>{t('SV Cloud is built by SV Academy.')}</span>
          </p>

          {/* The address on its own line rather than wrapped around the words
              above it. One string stays one string, so a translator can put
              the sentence in Thai word order without carrying an anchor tag
              through the middle of it. */}
          <a className="link-inline site-foot__host" href={SV_ACADEMY_URL} rel="noreferrer">
            {SV_ACADEMY_HOST}
          </a>
        </div>

        <nav className="site-foot__col" aria-labelledby="site-foot-product">
          <h2 className="site-foot__col-title" id="site-foot-product">
            {t('Product')}
          </h2>
          <ul className="site-foot__list">
            <li>
              {/* A span, exactly as in the landing top bar. The docs are not
                  written yet, and a link to a page that does not exist is the
                  first small lie a person catches us in. */}
              <span className="site-foot__soon">{t('Documentation')}</span>
            </li>
            <li>
              {isLoggedIn ? (
                <Link className="site-foot__link" to="/projects">
                  {t('Go to project')}
                </Link>
              ) : (
                <Link className="site-foot__link" to="/sign-in">
                  {t('Sign in')}
                </Link>
              )}
            </li>
            <li>
              <Link className="site-foot__link" to="/waitlist">
                {t('Join the waitlist')}
              </Link>
            </li>
            <li>
              <Link className="site-foot__link" to="/privacy">
                {t('Privacy policy')}
              </Link>
            </li>
            <li>
              <Link className="site-foot__link" to="/terms">
                {t('Terms of service')}
              </Link>
            </li>
            <li>
              <PrivacyChoicesLink className="site-foot__link site-foot__button" />
            </li>
          </ul>
        </nav>

        <nav className="site-foot__col" aria-labelledby="site-foot-social">
          <h2 className="site-foot__col-title" id="site-foot-social">
            {t('SV Academy')}
          </h2>
          <ul className="site-foot__list">
            {social.map((item) => (
              <li key={item.href}>
                {/* No target, so the tab the person is reading stays theirs, and
                    no new tab warning is owed to a screen reader. rel keeps the
                    referrer at home: a footer one click from the privacy page
                    should not be the thing that tells Instagram which page the
                    click came from. */}
                <a className="link-inline" href={item.href} rel="noreferrer">
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="site-foot__legal">
          <p className="site-foot__company mono">
            {t('Silicon Valley Innovation Co., Ltd. Bangkok, Thailand.')}
          </p>
          <p className="site-foot__company-th" lang="th">
            {COMPANY_TH}
          </p>
        </div>
      </div>
    </footer>
  );
}
